import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, MapPin, Clock, Euro, Users, Heart, Share2, Navigation,
  Sun, Cloud, CloudRain, Snowflake, Phone, MessageCircle, Calendar,
  ChevronRight, ExternalLink, Info, CheckCircle, AlertTriangle,
  Compass, Waves, Mountain, Utensils, Sparkles, ShoppingBag, Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '393934957532'; // Numero WhatsApp della Maisonette

// Weather icons mapping
const getWeatherIcon = (condition, size = 'w-5 h-5') => {
  switch (condition) {
    case 'sunny': return <Sun className={`${size} text-yellow-500`} />;
    case 'cloudy': return <Cloud className={`${size} text-gray-500`} />;
    case 'rainy': return <CloudRain className={`${size} text-blue-500`} />;
    case 'cold': return <Snowflake className={`${size} text-cyan-500`} />;
    default: return <Sun className={`${size} text-yellow-500`} />;
  }
};

// Category icons mapping
const getCategoryIcon = (category) => {
  switch (category) {
    case 'spiaggia': return <Waves className="w-5 h-5" />;
    case 'cultura': return <Compass className="w-5 h-5" />;
    case 'natura': return <Mountain className="w-5 h-5" />;
    case 'gastronomia': return <Utensils className="w-5 h-5" />;
    case 'relax': return <Sparkles className="w-5 h-5" />;
    case 'shopping': return <ShoppingBag className="w-5 h-5" />;
    default: return <Compass className="w-5 h-5" />;
  }
};

// Category label mapping
const getCategoryLabel = (category) => {
  const labels = {
    'spiaggia': 'Spiaggia',
    'cultura': 'Cultura',
    'natura': 'Natura',
    'gastronomia': 'Gastronomia',
    'relax': 'Relax',
    'shopping': 'Shopping'
  };
  return labels[category] || category;
};

// Weather condition label
const getWeatherLabel = (condition) => {
  const labels = {
    'sunny': 'Soleggiato',
    'cloudy': 'Nuvoloso',
    'rainy': 'Pioggia',
    'cold': 'Freddo'
  };
  return labels[condition] || condition;
};

// Skeleton loader
const DetailSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-64 md:h-96 bg-gray-200" />
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-8" />
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
    </div>
  </div>
);

export default function ItineraryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [itinerary, setItinerary] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Load favorite status
  useEffect(() => {
    const savedFavorites = localStorage.getItem('itinerary_favorites');
    if (savedFavorites) {
      const favorites = JSON.parse(savedFavorites);
      setIsFavorite(favorites.includes(id));
    }
  }, [id]);
  
  // Fetch itinerary
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch weather
        const weatherRes = await fetch(`${BACKEND_URL}/api/itineraries/suggested`);
        if (weatherRes.ok) {
          const data = await weatherRes.json();
          setWeather(data.meteo);
        }
        
        // Fetch all itineraries and find the one with matching id
        const itinerariesRes = await fetch(`${BACKEND_URL}/api/itineraries`);
        if (itinerariesRes.ok) {
          const data = await itinerariesRes.json();
          const found = data.find(it => it.id === id);
          if (found) {
            setItinerary(found);
          } else {
            setError('Itinerario non trovato');
          }
        } else {
          throw new Error('Errore nel caricamento');
        }
      } catch (err) {
        console.error('Error fetching itinerary:', err);
        setError('Impossibile caricare l\'itinerario');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Toggle favorite
  const toggleFavorite = () => {
    const savedFavorites = localStorage.getItem('itinerary_favorites');
    let favorites = savedFavorites ? JSON.parse(savedFavorites) : [];
    
    if (isFavorite) {
      favorites = favorites.filter(f => f !== id);
    } else {
      favorites.push(id);
    }
    
    localStorage.setItem('itinerary_favorites', JSON.stringify(favorites));
    setIsFavorite(!isFavorite);
  };
  
  // Share functionality
  const handleShare = async () => {
    const shareData = {
      title: itinerary?.nome || 'Itinerario La Maisonette',
      text: itinerary?.descrizione || 'Scopri questo itinerario!',
      url: window.location.href
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copiato negli appunti!');
  };
  
  // WhatsApp message
  const openWhatsApp = (type = 'info') => {
    const message = type === 'booking'
      ? `Buongiorno, vorrei richiedere una prenotazione per l'esperienza: ${itinerary?.nome}`
      : `Buongiorno, vorrei ricevere maggiori informazioni sull'itinerario: ${itinerary?.nome}`;
    
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };
  
  // Open Google Maps
  const openMaps = () => {
    if (itinerary?.link_esterno) {
      window.open(itinerary.link_esterno, '_blank');
    } else {
      const query = encodeURIComponent(`${itinerary?.luogo}, Italia`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    }
  };
  
  // Get default image
  const getImage = () => {
    if (itinerary?.immagine_url) return itinerary.immagine_url;
    
    // Category-based default images
    const defaults = {
      'spiaggia': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&h=600&fit=crop',
      'cultura': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200&h=600&fit=crop',
      'natura': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&h=600&fit=crop',
      'gastronomia': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&h=600&fit=crop',
      'relax': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&h=600&fit=crop',
      'shopping': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=600&fit=crop'
    };
    
    return defaults[itinerary?.categoria] || defaults['cultura'];
  };
  
  // Check if recommended today
  const isRecommended = weather && itinerary && weather.condizione === itinerary.condizione_meteo;
  
  if (loading) {
    return (
      <Layout>
        <DetailSkeleton />
      </Layout>
    );
  }
  
  if (error || !itinerary) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h1 className="font-cinzel text-2xl text-[#1A202C] mb-4">
              {error || 'Itinerario non trovato'}
            </h1>
            <p className="font-manrope text-[#4A5568] mb-6">
              L&apos;itinerario che stai cercando non esiste o non è più disponibile.
            </p>
            <Link to="/itineraries">
              <Button className="bg-[#C5A059] hover:bg-[#B08D45]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna agli itinerari
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="min-h-screen bg-[#F9F9F7]">
        {/* Hero Image */}
        <div className="relative h-64 md:h-96">
          <img
            src={getImage()}
            alt={itinerary.nome}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200&h=600&fit=crop';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          {/* Back button */}
          <Link 
            to="/itineraries"
            className="absolute top-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#1A202C]" />
          </Link>
          
          {/* Actions */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              onClick={toggleFavorite}
              className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-[#1A202C]'}`} />
            </button>
            <button
              onClick={handleShare}
              className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <Share2 className="w-5 h-5 text-[#1A202C]" />
            </button>
          </div>
          
          {/* Recommended badge */}
          {isRecommended && (
            <div className="absolute bottom-4 left-4 bg-[#C5A059] text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Consigliato oggi
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Category & Weather badge */}
          <div className="flex flex-wrap gap-3 mb-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#C5A059]/10 text-[#C5A059] rounded-full text-sm font-medium">
              {getCategoryIcon(itinerary.categoria)}
              {getCategoryLabel(itinerary.categoria)}
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-[#4A5568] rounded-full text-sm">
              {getWeatherIcon(itinerary.condizione_meteo, 'w-4 h-4')}
              Ideale con {getWeatherLabel(itinerary.condizione_meteo)}
            </span>
          </div>
          
          {/* Title */}
          <h1 className="font-cinzel text-3xl md:text-4xl text-[#1A202C] mb-4">
            {itinerary.nome}
          </h1>
          
          {/* Meta info */}
          <div className="flex flex-wrap gap-4 mb-6 text-[#4A5568]">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#C5A059]" />
              <span>{itinerary.luogo || 'Paestum'}</span>
            </div>
            
            {itinerary.durata && (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#C5A059]" />
                <span>{itinerary.durata}</span>
              </div>
            )}
            
            {itinerary.distanza_km && (
              <div className="flex items-center gap-2">
                <Navigation className="w-5 h-5 text-[#C5A059]" />
                <span>{itinerary.distanza_km} km dalla Maisonette</span>
              </div>
            )}
            
            {itinerary.costo_indicativo && (
              <div className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-[#C5A059]" />
                <span>{itinerary.costo_indicativo}</span>
              </div>
            )}
          </div>
          
          {/* Description */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="font-manrope text-[#4A5568] leading-relaxed">
              {itinerary.descrizione}
            </p>
          </div>
          
          {/* Why recommended today */}
          {isRecommended && weather && (
            <div className="bg-[#C5A059]/10 border border-[#C5A059]/20 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#C5A059]/20 rounded-full">
                  {getWeatherIcon(weather.condizione, 'w-6 h-6')}
                </div>
                <div>
                  <h3 className="font-cinzel text-lg text-[#1A202C] mb-2">
                    Perché è consigliato oggi
                  </h3>
                  <p className="font-manrope text-[#4A5568]">
                    Con {weather.temperatura}°C e cielo {weather.descrizione?.toLowerCase()}, 
                    questo itinerario è perfetto per oggi! 
                    {itinerary.categoria === 'spiaggia' && ' Le condizioni del mare sono ideali per una giornata in spiaggia.'}
                    {itinerary.categoria === 'cultura' && ' La luce è perfetta per ammirare i monumenti storici.'}
                    {itinerary.categoria === 'natura' && ' Le condizioni meteo sono ideali per un\'escursione nella natura.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Additional info sections */}
          {itinerary.cosa_portare && (
            <div className="mb-8">
              <h3 className="font-cinzel text-xl text-[#1A202C] mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                Cosa portare
              </h3>
              <p className="font-manrope text-[#4A5568]">{itinerary.cosa_portare}</p>
            </div>
          )}
          
          {itinerary.indicazioni && (
            <div className="mb-8">
              <h3 className="font-cinzel text-xl text-[#1A202C] mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-[#C5A059]" />
                Indicazioni utili
              </h3>
              <p className="font-manrope text-[#4A5568]">{itinerary.indicazioni}</p>
            </div>
          )}
          
          {/* Stages/Timeline */}
          {itinerary.tappe && itinerary.tappe.length > 0 && (
            <div className="mb-8">
              <h3 className="font-cinzel text-xl text-[#1A202C] mb-4">
                Itinerario a tappe
              </h3>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#C5A059]/30" />
                
                <div className="space-y-6">
                  {itinerary.tappe.map((tappa, index) => (
                    <div key={index} className="relative pl-12">
                      {/* Timeline dot */}
                      <div className="absolute left-2 top-1 w-5 h-5 bg-[#C5A059] rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 shadow-soft">
                        {tappa.orario && (
                          <span className="text-xs text-[#C5A059] font-medium">{tappa.orario}</span>
                        )}
                        <h4 className="font-cinzel text-lg text-[#1A202C] mt-1">{tappa.titolo}</h4>
                        {tappa.descrizione && (
                          <p className="font-manrope text-sm text-[#4A5568] mt-2">{tappa.descrizione}</p>
                        )}
                        {tappa.durata && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#4A5568] mt-2">
                            <Clock className="w-3 h-3" /> {tappa.durata}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            <Button
              onClick={openMaps}
              className="bg-[#1A202C] hover:bg-[#2D3748] flex items-center gap-2"
            >
              <Navigation className="w-4 h-4" />
              Apri su Google Maps
            </Button>
            
            <Button
              onClick={() => openWhatsApp('info')}
              variant="outline"
              className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Richiedi informazioni
            </Button>
            
            {itinerary.prenotabile && (
              <Button
                onClick={() => openWhatsApp('booking')}
                className="bg-[#C5A059] hover:bg-[#B08D45] flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Richiedi prenotazione
              </Button>
            )}
          </div>
          
          {/* Weather warning */}
          {!isRecommended && weather && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-manrope text-sm text-amber-800">
                  Questo itinerario è ideale con tempo {getWeatherLabel(itinerary.condizione_meteo).toLowerCase()}.
                  Oggi il meteo è {weather.descrizione?.toLowerCase()}. 
                  Valuta se le condizioni sono adatte alle tue preferenze.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Back to list */}
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <Link 
            to="/itineraries"
            className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#B08D45] font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna a tutti gli itinerari
          </Link>
        </div>
      </div>
    </Layout>
  );
}
